/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './WeaveTypeTable.scss';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import { mainColors } from '../../helpers/colors';
import {
  CCard,
  CCardBody,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CPagination,
  CPaginationItem,
  CBadge,
  CTooltip,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilArrowTop, cilArrowBottom, cilPencil, cilTrash } from '@coreui/icons';
import { deleteWeaveTypeRequestAction } from '../../store/weaveType/weaveTypeActions';
import { useTranslation } from '../../hooks/useTranslation';

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] === null || b[orderBy] === undefined) {
    return -1;
  }
  if (a[orderBy] === null || a[orderBy] === undefined) {
    return 1;
  }
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

function WeaveTypeTable(props) {
  const { 
    weaveTypeList,
    weaveTypeData,
    changeSortType,
    changeSortKey,
    weaveTypeHistorySortData: _weaveTypeHistorySortData,
    deleteWeaveTypeRequest
  } = props;
  
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getHeaderProps, getResizerFor } = useResizableColumns(['index','WeaveTypeName','WeaveTypeDescription','actions'], 'WeaveTypeTable');
  
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableData, setTableData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });

  const columns = [
    { id: 'index', label: '#', sortable: false },
    { id: 'WeaveTypeName', label: t('weaveTypeName'), sortable: true },
    { id: 'WeaveTypeDescription', label: t('Description'), sortable: true },
    { id: 'actions', label: t('Actions'), sortable: false }
  ];

  useEffect(() => {
    if (weaveTypeList && Array.isArray(weaveTypeList)) {
      setTotalRows(weaveTypeList.length);
      setTableData(weaveTypeList);
    }
  }, [weaveTypeList]);

  const handleEdit = (item) => {
    console.log('Editing weave type:', item);
    navigate(`/weavetype/edit/${item.WeaveTypeId}`, {
      state: { weaveType: item }
    });
  };

  const handleDeleteClick = (item) => {
    setDeleteModal({ open: true, item });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.item) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;

      if (userId && userId !== 'undefined') {
        const deleteData = {
          SessionUserId: String(userId),
          WeaveTypeId: deleteModal.item.WeaveTypeId
        };

        console.log('Deleting weave type:', deleteData);
        deleteWeaveTypeRequest({ data: deleteData });
      } else {
        console.error('User session not found');
      }
    }
    setDeleteModal({ open: false, item: null });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, item: null });
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
    scrollToTableContainer();
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value);
    setPage(1);
    scrollToTableContainer();
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);

    if (changeSortType) changeSortType(newOrder);
    if (changeSortKey) changeSortKey(property);
  };

  function scrollToTableContainer() {
    const tableContainer = document.querySelector('.weavetype-table-container');
    if (tableContainer) {
      tableContainer.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const getSortedData = () => {
    const stabilizedData = stableSort(tableData, getComparator(order, orderBy));
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return stabilizedData.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(totalRows / rowsPerPage);
  
  const paginationItems = [];
  for (let i = 1; i <= totalPages; i++) {
    paginationItems.push(
      <CPaginationItem 
        key={i} 
        active={i === page} 
        onClick={() => handleChangePage(i)}
      >
        {i}
      </CPaginationItem>
    );
  }
  
  return (
    <div className="weavetype-table-container">
      <CCard className="mb-4">
        <CCardBody>
          <CTable hover responsive>
            <CTableHead>
              <CTableRow>
                {columns.map((column) => {
                  const headerProps = getHeaderProps(column.id);
                  return (
                    <CTableHeaderCell 
                      key={column.id}
                      className={`table-header-cell ${column.sortable ? 'sortable-header' : ''}`}
                      onClick={() => column.sortable && handleRequestSort(column.id)}
                      style={{ cursor: column.sortable ? 'pointer' : 'default', fontStyle: 'normal', paddingRight: '8px', ...(headerProps.style || {}) }}
                    >
                      {column.label}
                      {column.sortable && orderBy === column.id && (
                        <CIcon
                          icon={order === 'asc' ? cilArrowTop : cilArrowBottom}
                          size="sm"
                          className="ms-1"
                        />
                      )}
                      {getResizerFor(column.id)}
                    </CTableHeaderCell>
                  );
                })}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {getSortedData().length > 0 ? (
                getSortedData().map((item, index) => (
                  <CTableRow key={item.WeaveTypeId || index}>
                    <CTableDataCell style={{ fontStyle: 'normal' }}>
                      {(page - 1) * rowsPerPage + index + 1}
                    </CTableDataCell>
                    <CTableDataCell style={{ fontStyle: 'normal' }}>
                      <CBadge color="primary" shape="rounded-pill">
                        {item.WeaveTypeName}
                      </CBadge>
                    </CTableDataCell>
                    <CTableDataCell style={{ fontStyle: 'normal' }}>
                      <span title={item.WeaveTypeDescription} style={{ fontStyle: 'normal' }}>
                        {item.WeaveTypeDescription?.length > 50 
                          ? `${item.WeaveTypeDescription.substring(0, 50)}...`
                          : item.WeaveTypeDescription
                        }
                      </span>
                    </CTableDataCell>
                    <CTableDataCell style={{ fontStyle: 'normal' }}>
                      <div className="d-flex gap-2">
                        <CTooltip content={t('edit')}>
                          <div 
                            className="cursor-pointer p-1" 
                            onClick={() => handleEdit(item)}
                            style={{ 
                              borderRadius: '4px',
                              transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <CIcon
                              icon={cilPencil}
                              size="sm"
                              style={{ color: mainColors.primary }}
                            />
                          </div>
                        </CTooltip>
                        
                        <CTooltip content={t('delete')}>
                          <div 
                            className="cursor-pointer p-1" 
                            onClick={() => handleDeleteClick(item)}
                            style={{ 
                              borderRadius: '4px',
                              transition: 'background-color 0.2s',
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <CIcon
                              icon={cilTrash}
                              size="sm"
                              style={{ color: '#dc3545' }}
                            />
                          </div>
                        </CTooltip>
                      </div>
                    </CTableDataCell>
                  </CTableRow>
                ))
              ) : (
                <CTableRow>
                  <CTableDataCell colSpan={4} className="text-center py-4" style={{ fontStyle: 'normal' }}>
                    <div className="text-muted" style={{ fontStyle: 'normal' }}>
                      {weaveTypeData?.Result?.ErrorCode === 0 
                        ? t('noWeaveTypeFound') 
                        : t('Loading data...')
                      }
                    </div>
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>
          
          {totalRows > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted" style={{ fontStyle: 'normal' }}>{t('Show')}:</span>
                <select 
                  className="form-select form-select-sm" 
                  value={rowsPerPage}
                  onChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value, 10))}
                  style={{ width: 'auto', fontStyle: 'normal' }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-muted" style={{ fontStyle: 'normal' }}>{t('entries')}</span>
              </div>
              
              <CPagination>
                <CPaginationItem 
                  aria-label={t('Previous')} 
                  disabled={page === 1}
                  onClick={() => handleChangePage(page - 1)}
                >
                  <span aria-hidden="true">&laquo;</span>
                </CPaginationItem>
                {paginationItems}
                <CPaginationItem 
                  aria-label={t('Next')} 
                  disabled={page === totalPages}
                  onClick={() => handleChangePage(page + 1)}
                >
                  <span aria-hidden="true">&raquo;</span>
                </CPaginationItem>
              </CPagination>
              
              <div className="text-muted" style={{ fontStyle: 'normal' }}>
                {t('showing')} {Math.min((page - 1) * rowsPerPage + 1, totalRows)} {t('to')}{' '}
                {Math.min(page * rowsPerPage, totalRows)} {t('of')} {totalRows} {t('entries')}
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>

      <CModal visible={deleteModal.open} onClose={handleDeleteCancel}>
        <CModalHeader>
          <CModalTitle style={{ fontStyle: 'normal' }}>{t('Confirm Delete')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p style={{ fontStyle: 'normal' }}>{t('confirmDeleteWeaveTypeStatus')}</p>
          {deleteModal.item && (
            <div className="bg-light p-3 rounded" style={{ fontStyle: 'normal' }}>
              <strong>{t('weaveTypeName')}:</strong> {deleteModal.item.WeaveTypeName}<br/>
              <strong>{t('Description')}:</strong> {deleteModal.item.WeaveTypeDescription}<br/>
              <strong>{t('id')}:</strong> {deleteModal.item.WeaveTypeId}
            </div>
          )}
          <p className="text-danger mt-2 mb-0" style={{ fontStyle: 'normal' }}>
            <small>{t('This action cannot be undone.')}</small>
          </p>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleDeleteCancel} style={{ fontStyle: 'normal' }}>
            {t('Cancel')}
          </CButton>
          <CButton color="danger" onClick={handleDeleteConfirm} style={{ fontStyle: 'normal' }}>
            {t('Delete')}
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    weaveTypeList: state.weaveTypeReducer?.weaveTypeList || [],
    weaveTypeData: state.weaveTypeReducer?.weaveTypeData || null,
    weaveTypeHistorySortData: state.weaveTypeReducer?.weaveTypeHistorySortData || {},
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    deleteWeaveTypeRequest: (payload) => {
      dispatch(deleteWeaveTypeRequestAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(WeaveTypeTable);